import { selectIElementsSlice } from "@/store/ielements-slice-selector";
import {
  selectLightweightAncestor,
  selectLightweightElement,
  selectLightweightElementsSlice,
} from "@/store/lightweight-elements-selectors";
import type {
  LightweightElement,
  LightweightElementsState,
  LightweightElementsStore,
} from "@/store/lightweight-elements-slice";
import { curryAppSelector } from "@/store/reselect";
import type { RootState } from "@/store/store";
import { useAppSelector } from "@/store/store-hooks";
import {
  IElement,
  IElementAreaSection,
  IElementTypeHint,
  isIElementAreaSection,
} from "@faro-lotv/ielement-types";
import { selectAreaCaptureTreeDataSetIds } from "@faro-lotv/project-source";
import { createSelector } from "@reduxjs/toolkit";

/**
 * @returns true if the given element in the project is an area that is derived from capture tree dataset and can not be aligned in viewer
 * @param state The store containing the iElements and the lightweightElements slices
 * @param element the element to check
 */
export const selectDisableCaptureTreeAlignment = curryAppSelector(
  createSelector(
    [
      selectIElementsSlice,
      selectLightweightElementsSlice,
      (state, element: IElement | undefined) => element,
    ],
    (iElements, lightweightElements, element): boolean => {
      if (!element) {
        return true;
      }

      if (
        isIElementAreaSection(element) &&
        selectAreaCaptureTreeRelated(element)({
          iElements,
          lightweightElements,
        })
      ) {
        return true;
      }

      return false;
    },
  ),
);

/**
 * @returns the hook version of selectDisableCaptureTreeAlignment
 * @param element the IElement to check if Alignment should be disabled
 */
export function useDisableCaptureTreeAlignment(
  element: IElement | undefined,
): boolean {
  return useAppSelector(selectDisableCaptureTreeAlignment(element));
}

export const disableScaleAreaMessage =
  "This area is derived from a point cloud dataset. Its scale cannot be edited.";

export const disableAlignAreaMessage =
  "This area is derived from a point cloud dataset. It can be moved by aligning its corresponding point cloud.";

/**
 * @param lightweightElements The lightweight elements store
 * @param element the given IElement to check
 * @returns true if the given element is from the capture tree
 */
export const selectFromCaptureTree = curryAppSelector(
  createSelector(
    [
      selectLightweightElementsSlice,
      (
        lightweightElements: LightweightElementsStore,
        element: LightweightElement | IElement | undefined,
      ) => element,
    ],
    (lightweightElements: LightweightElementsState, element): boolean =>
      !!selectLightweightAncestor(
        element,
        (el) => el.typeHint === IElementTypeHint.captureTree,
      )({ lightweightElements }),
  ),
);

type State = Pick<RootState, "lightweightElements" | "iElements">;

const selectAreaCaptureTreeRelated = curryAppSelector(
  createSelector(
    [
      selectIElementsSlice,
      selectLightweightElementsSlice,
      (state: State, area: IElementAreaSection) => area,
    ],
    (iElements, lightweightElements, area): boolean => {
      const ids = selectAreaCaptureTreeDataSetIds({ iElements }, area.id);
      if (!ids) return false;
      return ids.some((value) => {
        const oneElement = selectLightweightElement(value)({
          lightweightElements,
        });
        if (
          oneElement &&
          selectFromCaptureTree(oneElement)({ lightweightElements })
        ) {
          return true;
        }
        return false;
      });
    },
  ),
);
